import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  appendVisibleFaqStructuredData,
  buildCanonical,
  buildRouteStructuredData,
} from '../../seo/routeSeo';

function PageMeta({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  structuredData,
  replaceStaticStructuredData = false,
}) {
  const location = useLocation();

  useEffect(() => {
    const managedNodes = [];
    const suppressedStructuredData = [];
    const resolvedCanonical = canonical || buildCanonical(location.pathname);
    const routeStructuredData = buildRouteStructuredData({
      pathname: location.pathname,
      title,
      description,
      canonical: resolvedCanonical,
      structuredData,
    });
    const resolvedStructuredData = appendVisibleFaqStructuredData(
      routeStructuredData,
      location.pathname,
      document,
    );

    const upsertManagedMeta = (attribute, key, content) => {
      if (!content) return;
      let node = document.querySelector(`meta[${attribute}="${key}"][data-page-meta="true"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        node.setAttribute('data-page-meta', 'true');
        document.head.appendChild(node);
      }
      node.setAttribute('content', content);
      managedNodes.push(node);
    };

    if (title) {
      document.title = title;
    }

    if (description) {
      let descriptionNode = document.querySelector('meta[name="description"]');
      if (!descriptionNode) {
        descriptionNode = document.createElement('meta');
        descriptionNode.setAttribute('name', 'description');
        document.head.appendChild(descriptionNode);
      }
      descriptionNode.setAttribute('content', description);
    }

    if (resolvedCanonical) {
      let canonicalNode = document.querySelector('link[rel="canonical"][data-page-meta="true"]');
      if (!canonicalNode) {
        canonicalNode = document.createElement('link');
        canonicalNode.setAttribute('rel', 'canonical');
        canonicalNode.setAttribute('data-page-meta', 'true');
        document.head.appendChild(canonicalNode);
      }
      canonicalNode.setAttribute('href', resolvedCanonical);
      managedNodes.push(canonicalNode);
    }

    upsertManagedMeta('property', 'og:type', ogType);
    upsertManagedMeta('property', 'og:title', title);
    upsertManagedMeta('property', 'og:description', description);
    upsertManagedMeta('property', 'og:url', resolvedCanonical);
    const shareImage = ogImage ? new URL(ogImage, resolvedCanonical).href : undefined;
    upsertManagedMeta('property', 'og:image', shareImage);
    upsertManagedMeta('name', 'twitter:card', 'summary');
    upsertManagedMeta('name', 'twitter:image', shareImage);
    upsertManagedMeta('name', 'twitter:title', title);
    upsertManagedMeta('name', 'twitter:description', description);

    if (replaceStaticStructuredData) {
      document.querySelectorAll('script[type="application/ld+json"]:not([data-page-meta="true"])').forEach((node) => {
        suppressedStructuredData.push(node);
        node.setAttribute('type', 'application/json');
        node.setAttribute('data-page-meta-suppressed', 'true');
      });
    }

    if (resolvedStructuredData) {
      let structuredDataNode = document.querySelector(
        'script[type="application/ld+json"][data-page-meta="true"]',
      );
      if (!structuredDataNode) {
        structuredDataNode = document.createElement('script');
        structuredDataNode.setAttribute('type', 'application/ld+json');
        structuredDataNode.setAttribute('data-page-meta', 'true');
        document.head.appendChild(structuredDataNode);
      }
      structuredDataNode.textContent = JSON.stringify(resolvedStructuredData);
      managedNodes.push(structuredDataNode);
    }

    document.documentElement.setAttribute('data-prerender-ready', 'true');

    return () => {
      managedNodes.forEach((node) => node.remove());
      suppressedStructuredData.forEach((node) => {
        node.setAttribute('type', 'application/ld+json');
        node.removeAttribute('data-page-meta-suppressed');
      });
      document.documentElement.removeAttribute('data-prerender-ready');
    };
  }, [
    title,
    description,
    canonical,
    ogType,
    ogImage,
    structuredData,
    replaceStaticStructuredData,
    location.pathname,
  ]);

  return null;
}

export default PageMeta;
